import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface ScannedProduct {
  title: string;
  price: string;
  image: string;
}
const DEFAULT_PRODUCTS: ScannedProduct[] = [{
  title: 'The Inherited House Survival Guide — eBook',
  price: '$14',
  image: 'https://cdn.magicpatterns.com/uploads/oX2LZCxnZrxMK6dkG7L1fj/The_inherited_house_iPad_iPhone_image.png'
}, {
  title: 'Ultimate Digital Edition — Ultimate eBook',
  price: '$33.74',
  image: 'https://cdn.magicpatterns.com/uploads/oX2LZCxnZrxMK6dkG7L1fj/The_inherited_house_iPad_iPhone_image.png'
}];
const NAMES = ['James', 'Mary', 'Robert', 'Patricia', 'John', 'Jennifer', 'Michael', 'Linda', 'David', 'Elizabeth', 'William', 'Barbara', 'Richard', 'Susan', 'Joseph', 'Jessica', 'Thomas', 'Sarah', 'Charles', 'Karen', 'Christopher', 'Lisa', 'Daniel', 'Nancy', 'Matthew', 'Betty', 'Anthony', 'Margaret', 'Mark', 'Sandra'];
const CITIES = ['New York, NY', 'Los Angeles, CA', 'Chicago, IL', 'Houston, TX', 'Phoenix, AZ', 'Philadelphia, PA', 'San Antonio, TX', 'San Diego, CA', 'Dallas, TX', 'San Jose, CA', 'Austin, TX', 'Jacksonville, FL', 'Fort Worth, TX', 'Columbus, OH', 'Charlotte, NC', 'San Francisco, CA', 'Indianapolis, IN', 'Seattle, WA', 'Denver, CO', 'Washington, DC'];
const TIMES = ['just now', '2 minutes ago', '5 minutes ago', '12 minutes ago', 'just now', '1 minute ago'];
export function RecentPurchaseToast() {
  const [products, setProducts] = useState<ScannedProduct[]>([]);
  const [currentToast, setCurrentToast] = useState<{
    id: number;
    name: string;
    city: string;
    product: ScannedProduct;
    time: string;
  } | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const recentNamesRef = useRef<string[]>([]);
  const toastIdCounter = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prefersReducedMotion = useReducedMotion();
  // Use curated product catalog directly
  useEffect(() => {
    setProducts(DEFAULT_PRODUCTS);
  }, []);
  // 2. Manage Toast Lifecycle
  useEffect(() => {
    if (products.length === 0) return;
    const showNextToast = () => {
      // Pick random product (weighted towards main product if using defaults)
      let selectedProduct: ScannedProduct;
      if (products === DEFAULT_PRODUCTS) {
        const rand = Math.random();
        if (rand < 0.7) {
          selectedProduct = products[0]; // 70% Base
        } else {
          selectedProduct = products[1]; // 30% Base + Bonuses
        }
      } else {
        selectedProduct = products[Math.floor(Math.random() * products.length)];
      }
      // Pick random name avoiding recent ones
      let availableNames = NAMES.filter(n => !recentNamesRef.current.includes(n));
      if (availableNames.length === 0) {
        availableNames = NAMES;
        recentNamesRef.current = []; // Reset if we run out
      }
      const selectedName = availableNames[Math.floor(Math.random() * availableNames.length)];
      // Update recent names history (keep last 10)
      recentNamesRef.current.push(selectedName);
      if (recentNamesRef.current.length > 10) {
        recentNamesRef.current.shift();
      }
      // Pick random city and time
      const selectedCity = CITIES[Math.floor(Math.random() * CITIES.length)];
      const selectedTime = TIMES[Math.floor(Math.random() * TIMES.length)];
      toastIdCounter.current += 1;
      setCurrentToast({
        id: toastIdCounter.current,
        name: selectedName,
        city: selectedCity,
        product: selectedProduct,
        time: selectedTime
      });
      setIsVisible(true);
      // Hide after 4 seconds
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        // Schedule next toast (8-15 seconds after hiding)
        const nextInterval = Math.floor(Math.random() * (15000 - 8000 + 1) + 8000);
        timeoutRef.current = setTimeout(showNextToast, nextInterval);
      }, 4000);
    };
    // First toast after 3 seconds
    timeoutRef.current = setTimeout(showNextToast, 3000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, [products]);
  const handleClose = () => {
    setIsVisible(false);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // Schedule next one normally
    const nextInterval = Math.floor(Math.random() * (15000 - 8000 + 1) + 8000);
    timeoutRef.current = setTimeout(() => {
      // Trigger effect dependency to run showNextToast logic again
      setProducts([...products]);
    }, nextInterval);
  };
  return <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm px-4 pointer-events-none" data-id="element-388">
      <AnimatePresence data-id="element-389">
        {isVisible && currentToast && <motion.div key={currentToast.id} initial={prefersReducedMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 50
      }} animate={prefersReducedMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} exit={prefersReducedMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="bg-white rounded-xl shadow-lg border border-slate-light/30 p-3 flex items-start gap-3 pointer-events-auto" data-id="element-390">
            <img src={currentToast.product.image} alt={currentToast.product.title} className="w-12 h-12 rounded-md object-cover shrink-0 border border-slate-light/20" data-id="element-391" />
            <div className="flex-1 min-w-0 pt-0.5" data-id="element-392">
              <p className="text-sm text-text-dark font-body leading-snug" data-id="element-393">
                <span className="font-semibold text-navy" data-id="element-394">
                  {currentToast.name}
                </span>{' '}
                in {currentToast.city} just purchased{' '}
                <span className="font-medium text-navy" data-id="element-395">
                  {currentToast.product.title}
                </span>{' '}
                — {currentToast.product.price}
              </p>
              <p className="text-xs text-text-muted mt-1 font-body" data-id="element-396">
                {currentToast.time}
              </p>
            </div>
            <button onClick={handleClose} className="text-slate hover:text-navy transition-colors shrink-0 p-1 -mr-1 -mt-1" aria-label="Close notification" data-id="element-397">
              <XIcon className="w-4 h-4" data-id="element-398" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}